import { store } from "store"
import axios, { AxiosError } from "axios"
import { BaseQueryFn } from "@reduxjs/toolkit/query"
import { setStatus } from "store/slices/authSlice"
import { captureException } from "utils/sentry"

const getApiBaseUrl = () => {
  const host = window.location.host

  if (host === "labrador.network") {
    return import.meta.env.VITE_BACKEND_PROXY
  }

  return import.meta.env.VITE_BACKEND
}

const axiosInstance = axios.create({
  baseURL: `${getApiBaseUrl()}/api/v1`,
  headers: {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": true,
    Authorization: `Bearer ${localStorage.getItem("accessToken")}`
  }
})

interface AxiosBaseQueryArgs {
  url: string
  headers?: Record<string, string>
  method: string
  data?: any
  params?: any
}

export const axiosBaseQuery =
  (): BaseQueryFn<AxiosBaseQueryArgs, unknown, unknown> =>
  async ({ url, headers, method, data, params }) => {
    try {
      const isFormData = data instanceof FormData

      const result = await axiosInstance({
        url,
        headers: {
          "Content-Type": isFormData
            ? "multipart/form-data"
            : "application/json",
          ...headers
        },
        method,
        data,
        params
      })
      return { data: result?.data }
    } catch (error) {
      const axiosError = error as AxiosError

      const status = axiosError.response?.status
      captureException(error, {
        url,
        method,
        status,
        responseData: axiosError.response?.data
      })

      return {
        error: {
          status,
          data: axiosError.response?.data,
          message: axiosError.message
        }
      }
    }
  }

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      store.dispatch(setStatus("error"))
    }
    return Promise.reject(error)
  }
)

export const setAuthHeader = (token: string) => {
  axiosInstance.defaults.headers["Authorization"] = `Bearer ${token}`
}

export default axiosInstance
